// Campo WhatsApp do cadastro de cliente: NAO aceita digitacao livre direto.
// Click abre modal pra escolher contato existente do WhatsApp (atendimento+compras)
// ou digitar manualmente em modo separado. Reduz erro de digitar numero errado.

import { useEffect, useRef, useState } from 'react';
import { Search, MessageCircle, X, Pencil, Headphones, ShoppingCart } from 'lucide-react';
import { Modal } from '../Modal';
import { Avatar } from '../Avatar';
import { api } from '../../lib/api';
import { useToast } from '../Toast';
import { useListaTeclado } from '../../lib/useListaTeclado';

interface Props {
  value: string | null | undefined;  // digitos crus
  onChange: (digitos: string) => void;
  // Disparado ao escolher um contato da busca (nao no modo manual): nome ja LIMPO
  // (maiusculas + sem emoji). Quem usa decide se preenche o campo Nome do cadastro.
  onNomeContato?: (nomeLimpo: string) => void;
  placeholder?: string;
}

// Tira emojis (e modificadores) e devolve em MAIUSCULAS — pra importar o nome do
// contato do WhatsApp pro campo Nome do cliente sem o "😎 ... 😎".
export function limparNomeContato(nome: string): string {
  return (nome || '')
    .replace(/[\p{Extended_Pictographic}\p{Regional_Indicator}‍️⃣]/gu, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();
}

interface ContatoBusca {
  id: number;
  jid: string;
  inbox: 'atendimento' | 'compras';
  nome: string;
  push_name: string | null;
  telefone: string | null;
  foto_url: string | null;
}

function formatar(s: string): string {
  const d = (s || '').replace(/\D/g, '').slice(0, 13);
  if (!d) return '';
  // Tira DDI 55 pra exibir formato local
  const local = d.startsWith('55') && d.length >= 12 ? d.slice(2) : d;
  if (local.length <= 2) return `(${local}`;
  if (local.length <= 6) return `(${local.slice(0, 2)}) ${local.slice(2)}`;
  if (local.length <= 10) return `(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`;
  return `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
}

export function WhatsAppPickerInput({ value, onChange, onNomeContato, placeholder }: Props) {
  const [aberto, setAberto] = useState(false);
  const display = formatar(value ?? '');

  return (
    <>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <button
          type="button"
          onClick={() => setAberto(true)}
          title="Clique pra escolher contato do WhatsApp"
          // Isto e um BOTAO (abre o seletor), nao um campo de digitar — entao veste o
          // btn-primario, o mesmo azul solido (com hover) dos "Buscar Informacoes"/
          // "Buscar endereco" ao lado. A classe traz a cor do tema; nada de hex aqui.
          // O verde da marca do WhatsApp saiu de proposito (daltonismo do Ruan).
          className="btn-primario"
          // Largura do CONTEUDO, nao da coluna: esticado ate a borda ele virava uma
          // faixa azul do tamanho do formulario e roubava a atencao dos campos.
          style={{
            flex: '0 0 auto', maxWidth: '100%',
            padding: '6px 12px',
            cursor: 'pointer',
            fontSize: 13,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <MessageCircle size={14} style={{ flexShrink: 0 }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {display || (placeholder || 'Clique pra escolher contato...')}
          </span>
        </button>
        {value && (
          <button
            type="button"
            title="Limpar"
            onClick={() => onChange('')}
            style={{ padding: '0 8px', borderRadius: 4, border: '1px solid var(--input-border)', background: 'transparent', cursor: 'pointer' }}
          >
            <X size={14} />
          </button>
        )}
      </div>

      {aberto && (
        <ModalPicker
          aberto={aberto}
          onFechar={() => setAberto(false)}
          onEscolher={(digitos, nomeContato) => {
            onChange(digitos);
            if (nomeContato && onNomeContato) onNomeContato(limparNomeContato(nomeContato));
            setAberto(false);
          }}
          valorAtual={value ?? ''}
        />
      )}
    </>
  );
}

interface ModalProps {
  aberto: boolean;
  onFechar: () => void;
  onEscolher: (digitos: string, nomeContato?: string) => void;
  valorAtual: string;
}

function ModalPicker({ aberto, onFechar, onEscolher, valorAtual }: ModalProps) {
  const toast = useToast();
  const [modo, setModo] = useState<'buscar' | 'manual'>('buscar');
  const [busca, setBusca] = useState('');
  const [resultados, setResultados] = useState<ContatoBusca[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [manual, setManual] = useState('');
  const inputBuscaRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!aberto) return;
    setBusca('');
    setResultados([]);
    setManual(valorAtual || '');
    setModo('buscar');
    setTimeout(() => inputBuscaRef.current?.focus(), 50);
  }, [aberto, valorAtual]);

  useEffect(() => {
    if (!aberto || modo !== 'buscar') return;
    const q = busca.trim();
    if (q.length < 2) { setResultados([]); return; }
    let cancelado = false;
    setBuscando(true);
    const t = setTimeout(async () => {
      try {
        // So inbox=atendimento: clientes da loja respondem por la. Compras e pra fornecedor.
        const lista = await api.get<ContatoBusca[]>('/api/contatos', {
          query: { busca: q, inbox: 'atendimento', limit: 30, sem_grupo: true },
        });
        if (!cancelado) setResultados(lista);
      } catch {
        if (!cancelado) setResultados([]);
      } finally {
        if (!cancelado) setBuscando(false);
      }
    }, 250);
    return () => { cancelado = true; clearTimeout(t); };
  }, [busca, aberto, modo]);

  function escolherContato(c: ContatoBusca) {
    // Extrai digitos do telefone ou JID
    let digs = (c.telefone || '').replace(/\D/g, '');
    if (!digs && c.jid) digs = c.jid.replace(/@.*/, '').replace(/\D/g, '');
    if (!digs) {
      toast.erro('Contato sem numero valido');
      return;
    }
    const nome = (c.nome || c.push_name || '').trim();
    onEscolher(digs, nome || undefined);
  }

  const teclado = useListaTeclado(resultados, escolherContato);

  function salvarManual() {
    const d = manual.replace(/\D/g, '');
    if (d.length < 10) {
      toast.erro('Numero muito curto (minimo 10 digitos)');
      return;
    }
    // Normaliza: prefixa 55 se necessario
    const final = d.startsWith('55') ? d : (d.length === 10 || d.length === 11 ? '55' + d : d);
    onEscolher(final);
  }

  return (
    <Modal
      aberto={aberto}
      onFechar={onFechar}
      titulo="Escolher WhatsApp"
      largura={520}
      rodape={<button type="button" className="btn-secundario" onClick={onFechar}>Cancelar</button>}
    >
      <div className="flex gap-1.5 mb-3 p-1 rounded-lg" style={{ backgroundColor: 'rgba(0,0,0,0.05)' }}>
        <button
          type="button"
          onClick={() => setModo('buscar')}
          className="flex-1 py-1.5 px-3 rounded text-sm font-medium transition flex items-center justify-center gap-1.5"
          style={{
            backgroundColor: modo === 'buscar' ? 'var(--panel-bg, #fff)' : 'transparent',
            color: modo === 'buscar' ? 'var(--page-fg, #000)' : 'inherit',
            boxShadow: modo === 'buscar' ? '0 1px 2px rgba(0,0,0,0.06)' : undefined,
          }}
        >
          <Search size={14} /> Buscar contato
        </button>
        <button
          type="button"
          onClick={() => setModo('manual')}
          className="flex-1 py-1.5 px-3 rounded text-sm font-medium transition flex items-center justify-center gap-1.5"
          style={{
            backgroundColor: modo === 'manual' ? 'var(--panel-bg, #fff)' : 'transparent',
            color: modo === 'manual' ? 'var(--page-fg, #000)' : 'inherit',
            boxShadow: modo === 'manual' ? '0 1px 2px rgba(0,0,0,0.06)' : undefined,
          }}
        >
          <Pencil size={14} /> Digitar manualmente
        </button>
      </div>

      {modo === 'buscar' && (
        <>
          <div className="relative mb-2">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 opacity-50" />
            <input
              ref={inputBuscaRef}
              type="text"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              onKeyDown={teclado.onKeyDown}
              placeholder="Nome ou numero..."
              className="w-full pl-8 pr-3 py-1.5 rounded border text-sm outline-none focus:ring-2 focus:ring-blue-400"
              style={{ borderColor: 'var(--input-border)', fontSize: '16px' }}
            />
          </div>
          {buscando && <div className="text-center py-3 opacity-60 text-xs">Buscando...</div>}
          {!buscando && busca.trim().length < 2 && (
            <div className="text-center py-6 opacity-60 text-xs">Digite ao menos 2 caracteres pra buscar.</div>
          )}
          {!buscando && busca.trim().length >= 2 && resultados.length === 0 && (
            <div className="text-center py-6 opacity-60 text-sm">
              Nenhum contato encontrado.
              <button type="button" onClick={() => { setManual(busca); setModo('manual'); }}
                className="block mx-auto mt-2 text-blue-600 hover:underline text-xs">
                Digitar "{busca}" manualmente
              </button>
            </div>
          )}
          {!buscando && resultados.length > 0 && (
            <div ref={teclado.listaRef} className="space-y-1 max-h-[45vh] overflow-y-auto pr-1">
              {resultados.map((c, idx) => {
                const titulo = c.nome || c.push_name || c.telefone || c.jid;
                const Icon = c.inbox === 'compras' ? ShoppingCart : Headphones;
                return (
                  <button
                    key={c.id}
                    data-idx={idx}
                    type="button"
                    onClick={() => escolherContato(c)}
                    onMouseEnter={() => teclado.setHighlight(idx)}
                    className={`w-full flex items-center gap-3 p-2 rounded text-left transition ${idx === teclado.highlight ? 'bg-blue-50' : 'hover:bg-black/5'}`}
                    style={{ border: '1px solid var(--border, rgba(0,0,0,0.08))' }}
                  >
                    <Avatar nome={titulo} fotoUrl={c.foto_url} tamanho={36} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="text-sm font-medium truncate">{titulo}</div>
                      {c.telefone && (
                        <div className="text-[11px] opacity-70 truncate">{c.telefone}</div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {modo === 'manual' && (
        <div>
          <p className="text-xs opacity-70 mb-2">
            Use so se o cliente ainda nao tem conversa no WhatsApp. Inclua DDD. O DDI 55 e adicionado automaticamente.
          </p>
          <label className="text-[11px] uppercase tracking-wide opacity-60 block mb-1">Numero</label>
          <input
            type="tel"
            inputMode="tel"
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            placeholder="(64) 99999-9999"
            className="w-full px-3 py-1.5 rounded border text-sm outline-none focus:ring-2 focus:ring-blue-400"
            style={{ borderColor: 'var(--input-border)', fontSize: '16px' }}
          />
          <button
            type="button"
            onClick={salvarManual}
            className="w-full mt-3 btn-primario py-2 rounded-lg"
          >
            Salvar numero
          </button>
        </div>
      )}
    </Modal>
  );
}
